import React, { useState, useEffect, useCallback } from 'react';

interface SocialPost {
  id: number;
  title: string;
  content: string;
  media_urls?: string[];
  hashtags: string[];
  target_platforms: string[];
  scheduled_for: string;
  status: 'draft' | 'scheduled' | 'publishing' | 'published' | 'failed' | 'cancelled';
  post_type: string;
  generated_by_ai: boolean;
  publish_results?: Record<string, unknown>;
  created_at: string;
}

// ── Platform config ──────────────────────────────────────────────────────────
const PLATFORMS: Record<string, { label: string; color: string; bg: string; text: string; icon: string }> = {
  facebook:           { label: 'Facebook',  color: '#1877F2', bg: 'bg-[#1877F2]', text: 'text-[#1877F2]', icon: 'f' },
  instagram:          { label: 'Instagram', color: '#E1306C', bg: 'bg-[#E1306C]', text: 'text-[#E1306C]', icon: 'ig' },
  linkedin:           { label: 'LinkedIn',  color: '#0A66C2', bg: 'bg-[#0A66C2]', text: 'text-[#0A66C2]', icon: 'in' },
  google_my_business: { label: 'Google',    color: '#34A853', bg: 'bg-[#34A853]', text: 'text-[#34A853]', icon: 'G' },
  tiktok:             { label: 'TikTok',    color: '#010101', bg: 'bg-gray-900',  text: 'text-gray-900',  icon: 'tt' },
  youtube:            { label: 'YouTube',   color: '#FF0000', bg: 'bg-red-600',   text: 'text-red-600',   icon: 'yt' },
};

const STATUS_DOT: Record<string, string> = {
  draft:      'bg-gray-300',
  scheduled:  'bg-blue-400',
  publishing: 'bg-yellow-400',
  published:  'bg-emerald-400',
  failed:     'bg-red-500',
  cancelled:  'bg-gray-200',
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Brouillon', scheduled: 'Planifié', publishing: 'Publication…',
  published: 'Publié', failed: 'Échec', cancelled: 'Annulé',
};

// ── Optimal posting times ────────────────────────────────────────────────────
// Sector keywords → bucket (FR)
// ── Helpers ──────────────────────────────────────────────────────────────────
function getDaysInMonth(year: number, month: number) { return new Date(year, month + 1, 0).getDate(); }
function getFirstDayOfMonth(year: number, month: number) {
  const d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1;
}

const MONTHS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const DAYS   = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];

// ── Platform icon badge ──────────────────────────────────────────────────────
const PlatformBadge: React.FC<{ platform: string; size?: 'sm' | 'md' }> = ({ platform, size = 'sm' }) => {
  const p = PLATFORMS[platform] || PLATFORMS.facebook;
  const cls = size === 'sm' ? 'w-4 h-4 text-[8px]' : 'w-5 h-5 text-[9px]';
  return (
    <span
      className={`${cls} ${p.bg} rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 uppercase`}
      title={p.label}
    >
      {p.icon.slice(0, 2)}
    </span>
  );
};

// ── Post mini-card (in calendar cell) ───────────────────────────────────────
const PostChip: React.FC<{ post: SocialPost; onClick: () => void }> = ({ post, onClick }) => {
  const platform = post.target_platforms?.[0] || 'facebook';
  const p = PLATFORMS[platform] || PLATFORMS.facebook;
  const time = post.scheduled_for
    ? new Date(post.scheduled_for).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : '';
  const hasImg = post.media_urls && post.media_urls.length > 0;

  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-lg overflow-hidden border border-gray-100 hover:shadow-md transition-all group"
      title={post.content.slice(0, 100)}
    >
      {/* Image strip */}
      {hasImg && (
        <div className="relative h-9 overflow-hidden">
          <img
            src={post.media_urls![0]}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            onError={(e) => { (e.target as HTMLImageElement).parentElement!.style.display = 'none'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          {/* Platform badge overlay on image */}
          <div className="absolute top-1 right-1">
            <PlatformBadge platform={platform} />
          </div>
        </div>
      )}
      {/* Info row */}
      <div className="flex items-center gap-1 px-1.5 py-1" style={{ borderLeft: `3px solid ${p.color}` }}>
        {!hasImg && <PlatformBadge platform={platform} />}
        <span className="text-[10px] text-gray-600 font-medium flex-1 truncate">{time}</span>
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_DOT[post.status] || 'bg-gray-300'}`} />
      </div>
    </button>
  );
};

// ── Post detail modal ────────────────────────────────────────────────────────
const PostModal: React.FC<{
  post: SocialPost;
  onClose: () => void;
  onSave: (d: Partial<SocialPost>) => Promise<void>;
  onDelete: () => Promise<void>;
  onPublishNow: () => Promise<void>;
}> = ({ post, onClose, onSave, onDelete, onPublishNow }) => {
  const [content, setContent]       = useState(post.content);
  const [scheduledFor, setScheduledFor] = useState(
    post.scheduled_for ? new Date(post.scheduled_for).toISOString().slice(0, 16) : ''
  );
  const [saving, setSaving]         = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting]     = useState(false);

  const platform = post.target_platforms?.[0] || 'facebook';
  const p = PLATFORMS[platform] || PLATFORMS.facebook;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Colour bar */}
        <div className="h-1.5 rounded-t-2xl" style={{ background: p.color }} />

        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex flex-wrap items-center gap-2">
              {post.target_platforms.map(pl => {
                const pp = PLATFORMS[pl] || PLATFORMS.facebook;
                return (
                  <span key={pl} className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium text-white" style={{ background: pp.color }}>
                    <span className="uppercase">{pp.icon}</span> {pp.label}
                  </span>
                );
              })}
              <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${STATUS_DOT[post.status]?.replace('bg-', 'bg-') || 'bg-gray-400'}`}
                style={{ background: post.status === 'published' ? '#10b981' : post.status === 'failed' ? '#ef4444' : post.status === 'scheduled' ? '#3b82f6' : '#9ca3af' }}>
                {STATUS_LABEL[post.status] || post.status}
              </span>
              {post.generated_by_ai && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-700">✦ IA</span>
              )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-2">×</button>
          </div>

          {/* Image */}
          {post.media_urls && post.media_urls.length > 0 && (
            <div className="mb-4 rounded-xl overflow-hidden border border-gray-100">
              <img
                src={post.media_urls[0]}
                alt=""
                className="w-full h-56 object-cover"
                onError={(e) => { (e.target as HTMLImageElement).parentElement!.style.display = 'none'; }}
              />
            </div>
          )}

          {/* Content */}
          <div className="mb-4">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Contenu</label>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              disabled={post.status === 'published'}
              rows={6}
              className="w-full border border-gray-200 rounded-xl p-3 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-gray-50 disabled:text-gray-500"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{content.length} car.</p>
          </div>

          {/* Hashtags */}
          {post.hashtags && post.hashtags.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-1">
              {post.hashtags.map((h, i) => (
                <span key={i} className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{h.startsWith('#') ? h : `#${h}`}</span>
              ))}
            </div>
          )}

          {/* Date */}
          {post.status !== 'published' && (
            <div className="mb-5">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Planifié pour</label>
              <input
                type="datetime-local"
                value={scheduledFor}
                onChange={e => setScheduledFor(e.target.value)}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>
          )}

          {/* Résultats */}
          {post.publish_results && Object.keys(post.publish_results).length > 0 && (
            <div className="mb-4 bg-gray-50 rounded-xl p-3 text-xs text-gray-500">
              <p className="font-semibold text-gray-700 mb-2">Résultats publication</p>
              {Object.entries(post.publish_results).map(([pl, res]: [string, any]) => (
                <div key={pl} className="flex items-center gap-2 mb-1">
                  <span className={`w-2 h-2 rounded-full ${res.success ? 'bg-emerald-400' : 'bg-red-400'}`} />
                  <span>{PLATFORMS[pl]?.label || pl}</span>
                  {res.error && <span className="text-red-400">— {res.error}</span>}
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-3 border-t border-gray-100">
            {post.status !== 'published' ? (
              <>
                <button onClick={async () => { setSaving(true); await onSave({ content, scheduled_for: scheduledFor ? new Date(scheduledFor).toISOString() : undefined }); setSaving(false); }}
                  disabled={saving}
                  className="flex-1 py-2.5 text-sm font-semibold bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-colors">
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
                <button onClick={async () => { setPublishing(true); await onPublishNow(); setPublishing(false); }}
                  disabled={publishing}
                  className="flex-1 py-2.5 text-sm font-semibold text-white rounded-xl disabled:opacity-50 transition-colors"
                  style={{ background: p.color }}>
                  {publishing ? 'Publication...' : 'Publier maintenant'}
                </button>
                <button onClick={async () => { if (!confirm('Supprimer ce post ?')) return; setDeleting(true); await onDelete(); setDeleting(false); }}
                  disabled={deleting}
                  className="py-2.5 px-3 text-sm text-red-500 hover:bg-red-50 rounded-xl transition-colors">
                  {deleting ? '...' : '🗑'}
                </button>
              </>
            ) : (
              <p className="text-sm text-emerald-600 font-semibold flex items-center gap-2">
                ✓ Publié le {new Date(post.scheduled_for || post.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main CalendarPage ────────────────────────────────────────────────────────
const CalendarPage: React.FC = () => {
  const today = new Date();
  const [currentYear, setCurrentYear]   = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [posts, setPosts]               = useState<SocialPost[]>([]);
  const [loading, setLoading]           = useState(true);
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null);
  const [platformFilter, setPlatformFilter] = useState<string>('all');

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const start = new Date(currentYear, currentMonth - 1, 1).toISOString();
      const end   = new Date(currentYear, currentMonth + 2, 0).toISOString();
      const res   = await fetch(`/api/posts/calendar?start=${start}&end=${end}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
      });
      const data = await res.json();
      if (data.success) setPosts(data.posts || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [currentYear, currentMonth]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentYear(y => y - 1); setCurrentMonth(11); }
    else setCurrentMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentYear(y => y + 1); setCurrentMonth(0); }
    else setCurrentMonth(m => m + 1);
  };

  const postsByDay = posts.reduce((acc, post) => {
    if (!post.scheduled_for) return acc;
    const day = new Date(post.scheduled_for).toISOString().slice(0, 10);
    if (platformFilter === 'all' || post.target_platforms.includes(platformFilter)) {
      if (!acc[day]) acc[day] = [];
      acc[day].push(post);
    }
    return acc;
  }, {} as Record<string, SocialPost[]>);

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay    = getFirstDayOfMonth(currentYear, currentMonth);

  const monthStart = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`;
  const monthEnd   = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
  const monthPosts = posts.filter(p => p.scheduled_for && p.scheduled_for.slice(0, 10) >= monthStart && p.scheduled_for.slice(0, 10) <= monthEnd);
  const publishedCount = monthPosts.filter(p => p.status === 'published').length;
  const scheduledCount = monthPosts.filter(p => p.status === 'scheduled').length;
  const usedPlatforms  = [...new Set(posts.flatMap(p => p.target_platforms))];

  const handleSavePost = async (postId: number, data: Partial<SocialPost>) => {
    try {
      const res = await fetch(`/api/posts/${postId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      const result = await res.json();
      if (result.success) { setPosts(prev => prev.map(p => p.id === postId ? { ...p, ...result.post } : p)); setSelectedPost(null); }
    } catch (e) { console.error(e); }
  };

  const handleDeletePost = async (postId: number) => {
    try {
      await fetch(`/api/posts/${postId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } });
      setPosts(prev => prev.filter(p => p.id !== postId));
      setSelectedPost(null);
    } catch (e) { console.error(e); }
  };

  const handlePublishNow = async (postId: number) => {
    try {
      const res = await fetch(`/api/posts/${postId}/publish`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } });
      const result = await res.json();
      if (result.post) { setPosts(prev => prev.map(p => p.id === postId ? { ...p, ...result.post } : p)); setSelectedPost(null); }
    } catch (e) { console.error(e); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-10">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Calendrier éditorial</h1>
          <p className="text-sm text-gray-500 mt-0.5">Posts planifiés automatiquement par l'IA</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Platform filter pills */}
          <button
            onClick={() => setPlatformFilter('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${platformFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Tous
          </button>
          {usedPlatforms.map(pl => {
            const p = PLATFORMS[pl];
            if (!p) return null;
            return (
              <button
                key={pl}
                onClick={() => setPlatformFilter(pl === platformFilter ? 'all' : pl)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all text-white"
                style={{
                  background: platformFilter === pl ? p.color : '#e5e7eb',
                  color: platformFilter === pl ? 'white' : '#6b7280',
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
          <p className="text-2xl font-bold text-gray-900">{monthPosts.length}</p>
          <p className="text-xs text-gray-400 mt-0.5">Posts ce mois</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
          <p className="text-2xl font-bold text-emerald-600">{publishedCount}</p>
          <p className="text-xs text-gray-400 mt-0.5">Publiés</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
          <p className="text-2xl font-bold text-blue-600">{scheduledCount}</p>
          <p className="text-xs text-gray-400 mt-0.5">Planifiés</p>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Month nav */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-500 text-xl">‹</button>
          <h2 className="text-lg font-semibold text-gray-900">{MONTHS[currentMonth]} {currentYear}</h2>
          <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-500 text-xl">›</button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-7 w-7 border-2 border-gray-200 border-t-blue-500" />
          </div>
        ) : (
          <div className="p-4">
            {/* Day headers */}
            <div className="grid grid-cols-7 mb-1">
              {DAYS.map(d => (
                <div key={d} className="text-center text-xs font-semibold text-gray-400 py-2 uppercase tracking-wide">{d}</div>
              ))}
            </div>

            {/* Day cells */}
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}

              {Array.from({ length: daysInMonth }).map((_, i) => {
                const dayNum  = i + 1;
                const dateKey = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                const dayPosts = postsByDay[dateKey] || [];
                const isToday  = dayNum === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear();
                const isPast   = new Date(dateKey) < new Date(today.toDateString());

                return (
                  <div
                    key={dateKey}
                    className={`min-h-[110px] rounded-xl p-1.5 border transition-all ${
                      isToday
                        ? 'border-blue-400 bg-blue-50/60'
                        : dayPosts.length > 0
                          ? 'border-gray-200 bg-white'
                          : isPast
                            ? 'border-transparent bg-gray-50/30'
                            : 'border-dashed border-gray-100 bg-white/60'
                    }`}
                  >
                    {/* Day number */}
                    <div className="flex items-center justify-between mb-1.5">
                      <span className={`text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full ${
                        isToday ? 'bg-blue-500 text-white' : isPast ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {dayNum}
                      </span>
                      {dayPosts.length > 0 && (
                        <span className="text-[9px] text-gray-400 font-medium">{dayPosts.length} post{dayPosts.length > 1 ? 's' : ''}</span>
                      )}
                    </div>

                    {/* Post chips */}
                    <div className="space-y-1">
                      {dayPosts.slice(0, 2).map(post => (
                        <PostChip key={post.id} post={post} onClick={() => setSelectedPost(post)} />
                      ))}
                      {dayPosts.length > 2 && (
                        <button
                          onClick={() => setSelectedPost(dayPosts[2])}
                          className="w-full text-center text-[10px] text-gray-400 hover:text-gray-600 py-0.5 hover:bg-gray-100 rounded-md transition-colors"
                        >
                          +{dayPosts.length - 2} autres
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="px-6 pb-4 flex flex-wrap items-center gap-4 border-t border-gray-50 pt-3">
          <div className="flex items-center gap-3">
            {Object.entries(STATUS_DOT).map(([s, cls]) => (
              <div key={s} className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${cls}`} />
                <span className="text-xs text-gray-400">{STATUS_LABEL[s]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>


      {/* Prochains posts */}
      {scheduledCount > 0 && (
        <div>
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Prochains posts ({scheduledCount})
          </h2>
          <div className="space-y-2">
            {monthPosts
              .filter(p => p.status === 'scheduled')
              .sort((a, b) => new Date(a.scheduled_for).getTime() - new Date(b.scheduled_for).getTime())
              .map(post => {
                const platform = post.target_platforms?.[0] || 'facebook';
                const p = PLATFORMS[platform] || PLATFORMS.facebook;
                const dt = new Date(post.scheduled_for);
                return (
                  <button
                    key={post.id}
                    onClick={() => setSelectedPost(post)}
                    className="w-full bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-left hover:shadow-md transition-all flex items-center gap-4 group"
                  >
                    {/* Thumbnail */}
                    {post.media_urls && post.media_urls.length > 0 ? (
                      <div className="relative w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                        <img src={post.media_urls[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        <div className="absolute bottom-1 right-1">
                          <PlatformBadge platform={platform} size="sm" />
                        </div>
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: p.color + '15' }}>
                        <span className="text-lg font-bold" style={{ color: p.color }}>{p.icon.toUpperCase()}</span>
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: p.color }}>{p.label}</span>
                        {post.generated_by_ai && <span className="text-xs text-violet-500 bg-violet-50 px-1.5 py-0.5 rounded-full">✦ IA</span>}
                        <span className="text-xs text-gray-400 ml-auto">
                          {dt.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })} à {dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 truncate">{post.content}</p>
                    </div>

                    <svg className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {/* Modal */}
      {selectedPost && (
        <PostModal
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
          onSave={async d => handleSavePost(selectedPost.id, d)}
          onDelete={async () => handleDeletePost(selectedPost.id)}
          onPublishNow={async () => handlePublishNow(selectedPost.id)}
        />
      )}
    </div>
  );
};

export default CalendarPage;
